/** Typed client for the auth Worker API. */

const BASE = import.meta.env.VITE_API_BASE_URL ?? '';

export type Tag = { key: string; value: string };

export type Application = {
  applicationId: string;
  name: string;
  tags: Tag[];
  createdAt: string;
  updatedAt: string;
  userCount?: number;
};

export type User = {
  id: string;
  tenantId: string;
  name: string;
  email: string;
  emailVerified: boolean;
  isMaster: boolean;
  tags: Tag[];
  createdAt: string;
  updatedAt: string;
  roles?: string[];
};

export type Permission = {
  id: string;
  tenantId: string;
  name: string;
  code: string;
  description: string;
  tags: Tag[];
  createdAt: string;
  updatedAt: string;
};

export type Role = {
  id: string;
  tenantId: string;
  name: string;
  description: string;
  permissions: Permission[];
  tags: Tag[];
  createdAt: string;
  updatedAt: string;
};

export type EmailDelivery = { delivered: boolean; provider: string; error?: string };

export type Superuser = { id: string; email: string; createdAt: string; updatedAt: string };

/** Carries the API's error code so callers can branch on 401 without parsing text. */
export class ApiError extends Error {
  constructor(readonly status: number, readonly code: string, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

type RequestOptions = { method?: string; body?: unknown; token?: string | null };

async function request<T>(path: string, { method = 'GET', body, token }: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (token) headers['Authorization'] = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(`${BASE}/api${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, 'network_error', 'Could not reach the auth service');
  }

  const text = await res.text();
  const payload = text ? safeParse(text) : {};

  if (!res.ok) {
    const err = (payload as { error?: { code?: string; message?: string } }).error;
    throw new ApiError(res.status, err?.code ?? 'error', err?.message ?? `Request failed (${res.status})`);
  }
  return payload as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return {};
  }
}

// --- superuser --------------------------------------------------------------

export const api = {
  superuserLogin: (email: string, password: string) =>
    request<{ token: string; expiresIn: number; superuser: Superuser }>('/superuser/login', {
      method: 'POST',
      body: { email, password },
    }),

  superuserMe: (token: string) => request<{ superuser: Superuser }>('/superuser/me', { token }),

  listSuperusers: (token: string) =>
    request<{ superusers: Superuser[]; total: number }>('/superuser', { token }),

  createSuperuser: (token: string, email: string, password: string) =>
    request<{ superuser: Superuser }>('/superuser', { method: 'POST', body: { email, password }, token }),

  deleteSuperuser: (token: string, id: string) =>
    request<{ deleted: boolean }>(`/superuser/${id}`, { method: 'DELETE', token }),

  // --- applications (superuser only) ---

  listApplications: (token: string, search = '') =>
    request<{ applications: Application[]; total: number }>(
      `/applications${search ? `?search=${encodeURIComponent(search)}` : ''}`,
      { token },
    ),

  getApplication: (token: string, id: string) =>
    request<{
      application: Application;
      masterUsers: User[];
      counts: { users: number; roles: number; permissions: number };
    }>(`/applications/${id}`, { token }),

  createApplication: (
    token: string,
    body: { name: string; masterEmail: string; masterName?: string; tags?: Tag[] },
  ) =>
    request<{
      application: Application;
      masterUser: User;
      emailDelivery: EmailDelivery;
      temporaryPassword?: string;
      defaultRole: { id: string; name: string };
      seededPermissions: string[];
    }>('/applications', { method: 'POST', body, token }),

  updateApplication: (token: string, id: string, body: { name?: string; tags?: Tag[] }) =>
    request<{ application: Application }>(`/applications/${id}`, { method: 'PATCH', body, token }),

  deleteApplication: (token: string, id: string) =>
    request<{ deleted: boolean }>(`/applications/${id}`, { method: 'DELETE', token }),

  resetMasterPassword: (token: string, id: string) =>
    request<{ reset: boolean; emailDelivery: EmailDelivery; temporaryPassword?: string }>(
      `/applications/${id}/master/reset-password`,
      { method: 'POST', token },
    ),

  // --- tenant auth ---

  login: (applicationId: string, email: string, password: string) =>
    request<{
      token: string;
      expiresIn: number;
      user: User;
      application: { applicationId: string; name: string } | null;
    }>('/auth/login', { method: 'POST', body: { applicationId, email, password } }),

  me: (token: string) =>
    request<{
      user: User;
      application: { applicationId: string; name: string } | null;
      permissions: string[];
      isMaster: boolean;
    }>('/auth/me', { token }),

  changePassword: (token: string, currentPassword: string, newPassword: string) =>
    request<{ changed: boolean }>('/auth/change-password', {
      method: 'POST',
      body: { currentPassword, newPassword },
      token,
    }),

  resendVerification: (token: string) =>
    request<{ sent: boolean }>('/auth/resend-verification', { method: 'POST', token }),

  // --- tenant resources ---

  listUsers: (token: string, search = '') =>
    request<{ users: User[]; total: number }>(
      `/users${search ? `?search=${encodeURIComponent(search)}` : ''}`,
      { token },
    ),

  getUser: (token: string, id: string) =>
    request<{ user: User; roles: Role[]; effectivePermissions: string[] }>(`/users/${id}`, { token }),

  createUser: (
    token: string,
    body: { name: string; email: string; password?: string; roleIds?: string[]; isMaster?: boolean; tags?: Tag[] },
  ) =>
    request<{ user: User; emailDelivery: EmailDelivery; temporaryPassword?: string }>('/users', {
      method: 'POST',
      body,
      token,
    }),

  updateUser: (
    token: string,
    id: string,
    body: { name?: string; email?: string; isMaster?: boolean; tags?: Tag[] },
  ) => request<{ user: User }>(`/users/${id}`, { method: 'PATCH', body, token }),

  deleteUser: (token: string, id: string) =>
    request<{ deleted: boolean }>(`/users/${id}`, { method: 'DELETE', token }),

  setUserRoles: (token: string, id: string, roleIds: string[]) =>
    request<{ roles: Role[] }>(`/users/${id}/roles`, { method: 'PUT', body: { roleIds }, token }),

  resetUserPassword: (token: string, id: string) =>
    request<{ reset: boolean; emailDelivery: EmailDelivery; temporaryPassword?: string }>(
      `/users/${id}/reset-password`,
      { method: 'POST', token },
    ),

  listRoles: (token: string, search = '') =>
    request<{ roles: Role[]; total: number }>(
      `/roles${search ? `?search=${encodeURIComponent(search)}` : ''}`,
      { token },
    ),

  createRole: (
    token: string,
    body: { name: string; description?: string; permissionIds?: string[]; tags?: Tag[] },
  ) => request<{ role: Role }>('/roles', { method: 'POST', body, token }),

  updateRole: (
    token: string,
    id: string,
    body: { name?: string; description?: string; permissionIds?: string[]; tags?: Tag[] },
  ) => request<{ role: Role }>(`/roles/${id}`, { method: 'PATCH', body, token }),

  deleteRole: (token: string, id: string) =>
    request<{ deleted: boolean }>(`/roles/${id}`, { method: 'DELETE', token }),

  listPermissions: (token: string, search = '') =>
    request<{ permissions: Permission[]; total: number }>(
      `/permissions${search ? `?search=${encodeURIComponent(search)}` : ''}`,
      { token },
    ),

  createPermission: (
    token: string,
    body: { name: string; code: string; description?: string; tags?: Tag[] },
  ) => request<{ permission: Permission }>('/permissions', { method: 'POST', body, token }),

  updatePermission: (
    token: string,
    id: string,
    body: { name?: string; code?: string; description?: string; tags?: Tag[] },
  ) => request<{ permission: Permission }>(`/permissions/${id}`, { method: 'PATCH', body, token }),

  deletePermission: (token: string, id: string) =>
    request<{ deleted: boolean }>(`/permissions/${id}`, { method: 'DELETE', token }),
};
